import {
    ArrowLineLeft16,
    ArrowLineRight16,
    ArrowRight16
} from '@honeybook/icons';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import HBButton, { IButtonProps } from '../HBButton/HBButton';
import HBIconButton from '../HBIconButton/HBIconButton';
import HBLinkButton from '../HBLinkButton/HBLinkButton';
import HBText from '../HBText/HBText';
import PaginationDots from '../common/PaginationDots';
import {
    HBCalloutFooterButtonActionProps,
    HBCalloutFooterProps
} from './HBCalloutFooter.types';
import { TSize } from '../../tokens/types';
import { useSize } from '../common/SizeProvider';

type Size = Exclude<TSize, 'xl'>;

const sizeStyles: Record<
    Size,
    { buttonSize: IButtonProps['size']; footerTopPadding?: string }
> = {
    sm: {
        buttonSize: 'sm',
        footerTopPadding: '8px'
    },
    md: {
        buttonSize: 'md',
        footerTopPadding: '12px'
    },
    lg: {
        buttonSize: 'lg',
        footerTopPadding: '16px'
    }
} as const;

function Carousel({
    steps,
    setCurrentStep,
    currentStep,
    autoRotate,
    autoRotateInterval,
    autoRotateResumeDelay,
    isHovering
}: {
    setCurrentStep: (step: number) => void;
    currentStep: number;
    steps: any[];
    autoRotate: boolean;
    autoRotateInterval: number;
    autoRotateResumeDelay: number;
    isHovering: boolean;
}) {
    const [isUserInteracting, setIsUserInteracting] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const handleUserInteraction = () => {
        setIsUserInteracting(true);

        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Set a timeout to re-enable autoRotate after 10s
        timeoutRef.current = setTimeout(() => {
            setIsUserInteracting(false);
        }, autoRotateResumeDelay);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!autoRotate || isUserInteracting || isHovering) {
            return;
        }

        const interval = setInterval(() => {
            setCurrentStep(
                currentStep < steps.length - 1 ? currentStep + 1 : 0
            );
        }, autoRotateInterval);

        return () => clearInterval(interval); // Cleanup on unmount
    }, [
        autoRotate,
        steps.length,
        setCurrentStep,
        currentStep,
        autoRotateInterval,
        isUserInteracting,
        isHovering
    ]);

    return (
        <FooterButton>
            <PaginationDots count={steps.length} activeIndex={currentStep} />
            <ButtonsContainer>
                <HBIconButton
                    label="Previous"
                    testIdName="callout-card-carousel-previous"
                    onClick={() => {
                        handleUserInteraction();
                        if (currentStep === 0) {
                            return;
                        }
                        setCurrentStep(currentStep - 1);
                    }}
                    disabled={currentStep === 0}
                    icon={ArrowLineLeft16}
                />
                <HBIconButton
                    label="Next"
                    testIdName="callout-card-carousel-next"
                    onClick={() => {
                        handleUserInteraction();
                        if (currentStep === steps.length - 1) {
                            return;
                        }
                        setCurrentStep(currentStep + 1);
                    }}
                    disabled={currentStep === steps.length - 1}
                    icon={ArrowLineRight16}
                />
            </ButtonsContainer>
        </FooterButton>
    );
}

function Buttons({
    ctaText,
    onCtaClick,
    onSecondaryCtaClick,
    secondaryCtaText,
    steps,
    setCurrentStep,
    currentStep
}: HBCalloutFooterButtonActionProps & {
    setCurrentStep: (step: number) => void;
    currentStep: number;
}) {
    const size = useSize();

    const primaryCtaClick = !!steps
        ? () => {
              if (currentStep === steps.length - 1) {
                  return;
              }
              setCurrentStep(currentStep + 1);
          }
        : onCtaClick;
    const secondaryCtaClick = !!steps
        ? () => {
              if (currentStep === 0) {
                  return;
              }
              setCurrentStep(currentStep - 1);
          }
        : onSecondaryCtaClick;

    const primaryCtaDisabled = !!steps
        ? currentStep === steps.length - 1
        : false;
    const secondaryCtaDisabled = !!steps ? currentStep === 0 : false;

    return (
        <FooterButton>
            <ButtonsContainer>
                <HBButton
                    testIdName="callout-card-cta"
                    onClick={primaryCtaClick}
                    size={sizeStyles[size].buttonSize}
                    disabled={primaryCtaDisabled}
                >
                    {ctaText}
                </HBButton>
                {secondaryCtaText && (
                    <HBButton
                        testIdName="callout-card-secondary-cta"
                        onClick={secondaryCtaClick}
                        buttonTheme="secondary-invisible-primary"
                        size={sizeStyles[size].buttonSize}
                        disabled={secondaryCtaDisabled}
                    >
                        {secondaryCtaText}
                    </HBButton>
                )}
            </ButtonsContainer>
            {!!steps?.length && (
                <HBText
                    type="subtitle12"
                    color="text.tertiary"
                    transform="uppercase"
                >
                    {currentStep + 1} of {steps.length}
                </HBText>
            )}
        </FooterButton>
    );
}

export function HBCalloutFooter({
    action,
    actionsProps,
    setCurrentStep,
    currentStep,
    steps,
    autoRotate,
    autoRotateInterval,
    autoRotateResumeDelay,
    isHovering
}: HBCalloutFooterProps & {
    setCurrentStep: (step: number) => void;
    currentStep: number;
    steps: any[];
    autoRotate: boolean;
    autoRotateInterval: number;
    autoRotateResumeDelay: number;
    isHovering?: boolean;
}) {
    const size = useSize();

    if (!action) {
        return null;
    }

    return (
        <Footer>
            {action === 'button' && (
                <Buttons
                    {...actionsProps}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    steps={steps}
                />
            )}
            {action === 'link-button' && (
                <HBLinkButton
                    size={sizeStyles[size].buttonSize}
                    testIdName="link-button"
                    onClick={actionsProps.onClick}
                    icon={<ArrowRight16 />}
                    iconPosition="end"
                >
                    {actionsProps.text}
                </HBLinkButton>
            )}
            {action === 'carousel' && !!steps && (
                <Carousel
                    {...actionsProps}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    steps={steps}
                    autoRotate={autoRotate}
                    autoRotateInterval={autoRotateInterval}
                    autoRotateResumeDelay={autoRotateResumeDelay}
                    isHovering={isHovering}
                />
            )}
        </Footer>
    );
}

// -------------------------------- Styles -------------------------------- //
const Footer = styled.div`
    padding-top: ${props => sizeStyles[props.theme.size].footerTopPadding};
`;

const FooterButton = styled(Footer)`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const ButtonsContainer = styled.div`
    display: flex;
    gap: 8px;
`;
